import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { MOCK_OFFICE_REMINDERS } from "@/data/mock/office";
import {
  mergeReminders,
  unreadReminderCount,
  useOfficeStore,
} from "@/store/office.store";
import { useAccessStore } from "@/store/access.store";
import { useAccess } from "@/hooks/useAccess";

function NotificationBell() {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const companyId = useAccessStore((s) => s.activeCompanyId);
  const { hasModule } = useAccess();
  const markRead = useOfficeStore((s) => s.markReminderRead);

  const notifications = useMemo(() => {
    if (!hasModule("phase2")) {
      return [
        {
          id: "n1",
          title: "POD pending",
          body: "MSKU7845123 awaiting upload",
          href: "/app/pod/pod-1",
        },
      ];
    }
    return mergeReminders(
      MOCK_OFFICE_REMINDERS.filter((r) => r.companyId === companyId),
    )
      .filter((r) => !r.read)
      .slice(0, 6)
      .map((r) => ({
        id: r.id,
        title: r.title,
        body: r.body,
        href: r.href ?? "/app/office/reminders",
      }));
  }, [companyId, hasModule]);

  const unread = hasModule("phase2")
    ? unreadReminderCount(companyId, MOCK_OFFICE_REMINDERS)
    : notifications.length;

  const open = Boolean(anchor);

  return (
    <>
      <IconButton
        data-testid="notifications-bell"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t("common.notifications")}
        color="inherit"
      >
        <Badge color="error" badgeContent={unread} max={99} invisible={!unread}>
          {unread ? (
            <NotificationsActiveIcon fontSize="small" />
          ) : (
            <NotificationsNoneIcon fontSize="small" />
          )}
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxWidth: "calc(100vw - 24px)",
              borderRadius: 2.5,
              mt: 1,
            },
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography sx={{ fontWeight: 800 }}>{t("notifications.title")}</Typography>
          {unread > 0 && (
            <Chip
              size="small"
              label={t("dataManagement.pending", { count: unread })}
              color="primary"
            />
          )}
        </Stack>
        <List sx={{ maxHeight: 380, overflow: "auto", py: 0 }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center", px: 2 }}>
              <Typography color="text.secondary">
                {t("dataManagement.caughtUp")}
              </Typography>
            </Box>
          ) : (
            notifications.map((n) => (
              <ListItemButton
                key={n.id}
                component={RouterLink}
                to={n.href}
                onClick={() => {
                  if (hasModule("phase2")) markRead(n.id);
                  setAnchor(null);
                }}
              >
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 600, fontSize: "0.86rem" }}>
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.72rem" }}
                    >
                      {n.body}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))
          )}
        </List>
        <Divider />
        <Box sx={{ p: 1.25 }}>
          <Button
            component={RouterLink}
            to="/app/office/reminders"
            fullWidth
            onClick={() => setAnchor(null)}
          >
            {t("notifications.viewAll")}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default NotificationBell;
